import React from 'react';
import { observable, action, computed } from 'mobx';
import FileUploadModal, { UploadFileStatus } from './FileUploadModal';
import ModalStore from '@app/src/common/store/ModalStore';
import FilesRepository from '@app/src/common/repository/FilesRepository';
import AuthStore from '@app/src/common/store/AuthStore';
// tslint:disable-next-line: no-submodule-imports no-implicit-dependencies
import Worker from 'worker-loader!../../../workers/FileUploadWorker';
import { WORKER_ACTIONS } from '@app/src/common/constants/workerActions';
import NotificationService from '@app/src/services/notification-service/NotificationService';
import { getConfirmModal } from '../ConfirmModal';
import PlayListRepository from '@app/src/common/repository/PlayListRepository';

export interface IUploadData {
  progress: number;
  worker: Worker;
  status: UploadFileStatus;
  fileName: string;
  fileType: string;
  fileSize: number;
}

class FileUploadModalService {
  @observable uploadFilesMap = new Map<string, IUploadData>();
  @observable collapsed = false;
  modalIsShown = false;
  updatePlaylists = false;

  private async checkFileInPlaylists(usedInPlaylists: boolean) {
    this.updatePlaylists = false;

    if (!usedInPlaylists) {
      return;
    }
    try {
      await getConfirmModal('gallery.reupload.confirmation', undefined, {
        cancelText: 'button.no',
        submitText: 'button.yes'
      } as any);
      this.updatePlaylists = true;
    } catch (e) {
      return;
    }
  }

  private async handleUploaded(
    fileId: string,
    onUpload: (fileId: string) => void
  ) {
    if (this.updatePlaylists) {
      await PlayListRepository.updateVideoDuration(fileId);
    }
    onUpload(fileId);
  }

  @action
  showModal(
    files: File[],
    folderId: string,
    onUpload: (fileId: string) => void
  ) {
    if (!this.modalIsShown) {
      this.modalIsShown = true;
      this.collapsed = false;
      ModalStore.showModal(props => <FileUploadModal {...props} />).catch(
        () => null
      );
    }
    this.uploadFiles(files, folderId, onUpload);
  }

  @action
  showReuploadModal(
    fileId: string,
    file: File,
    onUpload: (fileId: string) => void
  ) {
    if (!this.modalIsShown) {
      this.modalIsShown = true;
      this.collapsed = false;
      ModalStore.showModal(props => <FileUploadModal {...props} />).catch(
        () => null
      );
    }
    this.reuploadFile(fileId, file, onUpload);
  }

  @action
  closeModal() {
    this.terminateAllFileUploads();
    this.cleanUp();
    this.modalIsShown = false;
    ModalStore.closeModal();
  }

  @action
  cleanUp() {
    this.uploadFilesMap.clear();
  }

  @action
  terminateAllFileUploads() {
    this.uploadFilesMap.forEach(uploadData => uploadData.worker.terminate());
  }

  @action
  async uploadFiles(
    files: File[],
    folderId: string,
    onUpload: (fileId: string) => void
  ) {
    if (files) {
      files.map(async file => {
        const fileId = await FilesRepository.createFile(file, folderId);
        await this.uploadFile(fileId, file, onUpload);
      });
    }
  }

  @action
  async reuploadFile(
    fileId: string,
    file: File,
    onUpload: (fileId: string) => void
  ) {
    try {
      if (file && fileId) {
        const response = await FilesRepository.updateFile(fileId, file);
        await this.checkFileInPlaylists(response?.usedInPlaylists);
        await this.uploadFile(fileId, file, onUpload);
      }
    } catch (e) {
      this.closeModal();
      NotificationService.errorMessage('gallery.reupload.failed');
    }
  }

  @action
  async uploadFile(
    fileId: string,
    file: File,
    onUpload: (fileId: string) => void
  ) {
    if (file && fileId) {
      const fileMetaResponse = await FilesRepository.getFileMeta(fileId);
      const { jwtToken } = await AuthStore.checkAccess();
      const worker = new Worker();

      this.uploadFilesMap.set(fileId, {
        worker,
        progress: 0,
        status: UploadFileStatus.PROGRESS,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size
      });

      const onMessage = (event: MessageEvent) => {
        const { action: workerAction, uploadFileId, progress } = event.data;

        if (workerAction === WORKER_ACTIONS.UPLOAD_PROGRESS) {
          const data = this.uploadFilesMap.get(uploadFileId);

          if (data) {
            this.uploadFilesMap.set(uploadFileId, { ...data, progress });
          }
        }

        if (workerAction === WORKER_ACTIONS.FILE_UPLOADED) {
          worker.removeEventListener('message', onMessage);
          const status = UploadFileStatus.UPLOADED;
          const data = this.uploadFilesMap.get(uploadFileId);

          if (data) {
            this.uploadFilesMap.set(uploadFileId, { ...data, status });
            data.worker.terminate();

            if (this.isUploadEnded) {
              this.handleUploaded(fileId, onUpload);
            }
          }
        }

        if (workerAction === WORKER_ACTIONS.ERROR) {
          worker.removeEventListener('message', onMessage);
          const status = UploadFileStatus.FAILED;
          const data = this.uploadFilesMap.get(uploadFileId);

          if (data) {
            this.uploadFilesMap.set(uploadFileId, { ...data, status });
            data.worker.terminate();
          }
          return;
        }

        if (workerAction === WORKER_ACTIONS.REFRESH_TOKEN) {
          AuthStore.checkAccess()
            .then(access => {
              worker.postMessage({
                action: WORKER_ACTIONS.SET_TOKEN,
                jwtToken: access.jwtToken
              });
            })
            .catch(() => {
              worker.removeEventListener('message', onMessage);
              const status = UploadFileStatus.FAILED;
              const data = this.uploadFilesMap.get(uploadFileId);

              if (data) {
                this.uploadFilesMap.set(uploadFileId, { ...data, status });
                data.worker.terminate();
              }
            });
        }
      };

      worker.addEventListener('message', onMessage);
      worker.postMessage({
        action: WORKER_ACTIONS.UPLOAD,
        file: file as Blob,
        fileId,
        fileMeta: fileMetaResponse.data,
        jwtToken
      });
    }
  }

  @computed
  get isUploadEnded() {
    return [...this.uploadFilesMap.values()].every(
      val =>
        val.status === UploadFileStatus.UPLOADED ||
        val.status === UploadFileStatus.FAILED
    );
  }

  @computed
  get uploadedFilesCount() {
    return [...this.uploadFilesMap.values()].filter(
      val => val.status === UploadFileStatus.UPLOADED
    ).length;
  }
}

export default new FileUploadModalService();
